import type { TFunction } from 'i18next';

// Dates (07 §1.2): local time, `dd/MM HH:mm`; relative under 24 h ("3 h fa",
// "tra 40 min"); date-only values without time. `now` is injectable so the
// stories and the e2e photographs are stable.

const pad = (n: number) => String(n).padStart(2, '0');

export function dayMonth(date: Date): string {
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
}

export function dayMonthTime(date: Date): string {
  return `${dayMonth(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function clockTime(date: Date): string {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** "12 s", "40 min", "3 h", "2 g" (it) / "12 s", "40 min", "3 h", "2 d" (en). */
export function shortDuration(ms: number, t: TFunction): string {
  const s = Math.max(0, Math.round(Math.abs(ms) / 1000));
  if (s < 60) return t('time.s', { n: s });
  const m = Math.round(s / 60);
  if (m < 60) return t('time.min', { n: m });
  const h = Math.round(m / 60);
  if (h < 48) return t('time.h', { n: h });
  return t('time.d', { n: Math.round(h / 24) });
}

/** "12 s fa" / "tra 40 min" under 24 h, else `dd/MM HH:mm`. */
export function relative(date: Date, now: Date, t: TFunction): string {
  const diff = date.getTime() - now.getTime();
  if (Math.abs(diff) >= 86_400_000) return dayMonthTime(date);
  const d = shortDuration(diff, t);
  return diff >= 0 ? t('time.in', { d }) : t('time.ago', { d });
}

export const isoUtc = (date: Date) => `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
